import type {
  Control,
  FieldErrors,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { path } from 'ramda';

import * as Form from 'components/_core/form';
import { FieldProps } from 'components/_core/form';

import locales from './locales';
import { ibanUtils } from './utils';

type IbanInputFieldProps<T extends FieldValues> = Omit<
  FieldProps,
  'value' | 'onChange' | 'name' | 'defaultValue' | 'children'
> &
  Omit<UseControllerProps<T>, 'control'> & {
    control: Control<T>;
    placeholder?: string;
    onChange?: (value: string) => void;
  };

const IbanInputField = <T extends FieldValues>({
  control,
  defaultValue,
  name,
  onChange,
  placeholder,
  rules,
  shouldUnregister,
  ...props
}: IbanInputFieldProps<T>) => {
  return (
    <Form.Field
      error={
        path<FieldErrors<T>>(name.split('.'), control._formState.errors)
          ?.message as string | undefined
      }
      label={locales.iban}
      {...props}
    >
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({ field }) => (
          <Form.Input
            {...field}
            isDisabled={props.isDisabled}
            onChange={(e) => {
              const formattedValue = ibanUtils.unmask(e.target.value);
              field.onChange(formattedValue);
              onChange?.(formattedValue);
            }}
            placeholder={placeholder}
            value={ibanUtils.mask(field.value)}
          />
        )}
        rules={rules}
        shouldUnregister={shouldUnregister}
      />
    </Form.Field>
  );
};

export default IbanInputField;
